import React, { useState } from 'react';
import { getContext } from 'common';
import KEY_CODES from 'common/KeyCodes';

import SelectItem, {
  SelectItemText,
  SelectListItem,
  SelectItemButton,
  SelectItemButtonProps,
  SelectItemProps
} from 'components/SelectItem';

import Styled from './styles';

const HOURS_24 = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
const HOURS_12 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const { ENTER_KEY, SPACE_KEY } = KEY_CODES;
const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY);

const SELECT_ITEM_CONFIG = {
  menuProps: {
    usePortal: false
  },
  listProps: {
    style: {
      maxHeight: '15rem',
      minWidth: 'inherit'
    }
  },
};

type HourOnChange = (arg: { hour: number, event: React.MouseEvent | React.KeyboardEvent }) => void;

export interface CalendarHourPickerProps extends Omit<React.ComponentPropsWithRef<'div'>, 'onChange'> {
  /** The selected hour. */
  hour?: string;
  /** To overwrite the array of the valid hours items. By default, it renders 0 to 12 or 0 to 24 considering the `period` prop. */
  hoursItems?: React.ReactNode[];
  /** To overwrite the label and value of the hours options. */
  hours?: {
    label: string,
    value: number
  }[];
  /** Callback fired when the value is changed. It returns the value of the item and the triggering event, `function({ event, hour }) => void ` */
  onChange?: HourOnChange;
  /** To overwrite the `<SelectItem />` props. See `SelectItem` component documentation for full details. */
  selectItemProps?: Partial<SelectItemProps>;
  /** The period of the time. */
  period?: '12H' | '24H';
  /** To overwrite the `<SelectItemButton />` props. See `SelectItemButton` component documentation for full details. */
  selectItemButtonProps?: Partial<SelectItemButtonProps>;
  /** If true, the SelectItem will close when there is a click outside the dropdown. */
  selectItemCloseOnOutsideClick?: boolean;
}

/**
 * <CalendarHourPicker/>, any other props will be provided to the root element.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * To override the `theme`, use `selectItemProps` prop.
 */
const CalendarHourPicker = React.forwardRef<HTMLElement, CalendarHourPickerProps>((props: CalendarHourPickerProps, ref) =>
  getContext(({ RTL }) => {
    const {
      period,
      hour,
      hoursItems,
      hours: hoursProp,
      onChange,
      selectItemProps,
      selectItemButtonProps,
      selectItemCloseOnOutsideClick,
      ...otherProps
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const resolvedHours = hoursProp || (period === '24H'
      ? HOURS_24.map(hr => ({ value: hr, label: hr }))
      : HOURS_12.map(hr => ({ value: hr, label: hr })));

    const {
      onClick: onClickSelectItem,
      onKeyDown: onKeyDownSelectItem,
      ...otherSelectItemProps
    } = selectItemProps;

    const toggleSelectItem = (event) => {
      const { type } = event;
      if (type === 'keydown') {
        if (isSelectionKeyPressed(event.key)) {
          setIsOpen(!isOpen);
        }
        onKeyDownSelectItem && onKeyDownSelectItem(event);
      } else if (type === 'click') {
        setIsOpen(!isOpen);
        onClickSelectItem && onClickSelectItem(event);
      }
    };

    const handleSelectItemOnClose = () => {
      selectItemCloseOnOutsideClick && setIsOpen(false);
    };

    const renderSelectItemBase = internalProps => (
      <SelectItemButton
        {...internalProps}
        {...selectItemButtonProps}
      >
        {hour}
      </SelectItemButton>
    );
    const resolvedSelectItemProps = {
      truncateListText: false,
      renderSelectItemBase,
      isOpen,
      onClick: (event) => { toggleSelectItem(event); },
      onKeyDown: (event) => { toggleSelectItem(event); },
      onClose: () => { handleSelectItemOnClose(); },
      ...SELECT_ITEM_CONFIG,
      ...otherSelectItemProps
    };

    const handleItemEvent = ({ selectedHour, event }) => {
      const { type } = event;
      if (type === 'keydown') {
        if (isSelectionKeyPressed(event.key)) {
          onChange && onChange({ event, hour: selectedHour });
          setIsOpen(false);
        }
      } else if (type === 'click') {
        onChange && onChange({ event, hour: selectedHour });
        setIsOpen(false);
      }
    };

    const renderItem = hourItem => (
      <SelectListItem
        key={hourItem.label}
        selected={hour === hourItem.value}
        onClick={event => handleItemEvent({ selectedHour: hourItem.value, event })}
        onKeyDown={event => handleItemEvent({ selectedHour: hourItem.value, event })}
      >
        <SelectItemText>{hourItem.label}</SelectItemText>
      </SelectListItem>
    );

    return hour !== undefined ?
      (
        <Styled.CalendarHourPicker {...otherProps} re={ref} $rtl={RTL}>
          <SelectItem {...resolvedSelectItemProps}>
            {hoursItems || resolvedHours.map(hourItem => renderItem(hourItem))}
          </SelectItem>
        </Styled.CalendarHourPicker>
      )
      : null;
  }));

CalendarHourPicker.defaultProps = {
  hour: undefined,
  hoursItems: undefined,
  hours: undefined,
  onChange: undefined,
  selectItemProps: {},
  period: '12H',
  selectItemButtonProps: {},
  selectItemCloseOnOutsideClick: false,
};

CalendarHourPicker.displayName = 'CalendarHourPicker';

export default CalendarHourPicker;
